import { Link } from "react-router-dom";
import { ArrowRight, Search, MapPin, Target } from "lucide-react";
import { Button } from "@/components/ui/button";

interface LocalSEOServicesProps {
  language: 'es' | 'en';
}

export const LocalSEOServices = ({ language }: LocalSEOServicesProps) => {
  const content = {
    es: {
      title: "Servicios locales aplicados",
      subtitle: "Estrategias combinadas para maximizar tu visibilidad local.",
      services: [
        {
          icon: Search,
          name: "SEO Técnico",
          description: "Base técnica sólida para que Google pueda rastrear e indexar correctamente tu sitio.",
          link: "/servicios/seo-tecnico"
        },
        {
          icon: MapPin,
          name: "SEO Local",
          description: "Optimización de Google Business Profile, citaciones NAP, y contenido geolocalizado.",
          link: "/seo-local"
        },
        {
          icon: Target,
          name: "Google Ads Local",
          description: "Campañas de pago segmentadas geográficamente para resultados inmediatos.",
          link: "/servicios/google-ads"
        }
      ],
      cta: "Solicitar reunión inicial gratuita",
      ctaLink: "/diagnostico-seo-gratis"
    },
    en: {
      title: "Applied local services",
      subtitle: "Combined strategies to maximize your local visibility.",
      services: [
        {
          icon: Search,
          name: "Technical SEO",
          description: "Solid technical foundation so Google can properly crawl and index your site.",
          link: "/en/services/technical-seo"
        },
        {
          icon: MapPin,
          name: "Local SEO",
          description: "Google Business Profile optimization, NAP citations, and geolocated content.",
          link: "/local-seo"
        },
        {
          icon: Target,
          name: "Local Google Ads",
          description: "Geographically targeted paid campaigns for immediate results.",
          link: "/en/services/google-ads"
        }
      ],
      cta: "Request free initial consultation",
      ctaLink: "/en/free-seo-diagnosis"
    }
  };

  const t = content[language];

  return (
    <section className="section-padding bg-background">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-10">
          {t.services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Link 
                key={index}
                to={service.link}
                className="group bg-secondary/30 rounded-2xl p-6 border border-border hover:border-brand hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mb-4 group-hover:bg-brand/20 transition-colors">
                  <IconComponent className="w-6 h-6 text-brand" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-brand transition-colors">
                  {service.name}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {service.description}
                </p>
              </Link>
            );
          })}
        </div>
        
        <div className="text-center">
          <Button 
            size="lg" 
            className="btn-gradient group"
            asChild
          >
            <Link to={t.ctaLink}>
              {t.cta}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
